import React from "react";
import { Link } from "react-router-dom";
import Button from "../../ui/Button";
import { Card, CardBody, CardHeader } from "../../ui/Card";
import Input from "../../ui/Input";
import { createJob, deleteJob, deleteJobsBulk, listJobs } from "../../api/jobs";
import { extractCrmIntake } from "../../api/crm";
import { standardizeCapitalization } from "../../utils/textFormat";

function firstText(...values) {
  for (const value of values) {
    const text = String(value || "").trim();
    if (text) return text;
  }
  return "";
}

export default function JobsDashboard() {
  const [jobs, setJobs] = React.useState([]);
  const [query, setQuery] = React.useState("");
  const [newJobName, setNewJobName] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [busyDelete, setBusyDelete] = React.useState(false);
  const [selectedIds, setSelectedIds] = React.useState(() => new Set());
  const [smartIntakeText, setSmartIntakeText] = React.useState("");
  const [smartIntakeBusy, setSmartIntakeBusy] = React.useState(false);
  const [smartIntakeMessage, setSmartIntakeMessage] = React.useState("");

  const refresh = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const rows = await listJobs({ limit: 200, offset: 0 });
      setJobs(rows);
    } catch (e) {
      setError(e?.message || "Failed to load jobs");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  React.useEffect(() => {
    const valid = new Set(jobs.map((j) => String(j?.id || "")));
    setSelectedIds((prev) => {
      const next = new Set();
      prev.forEach((id) => {
        if (valid.has(id)) next.add(id);
      });
      return next;
    });
  }, [jobs]);

  const handleCreate = async () => {
    setBusy(true);
    setError("");
    try {
      await createJob(newJobName);
      setNewJobName("");
      await refresh();
    } catch (e) {
      setError(e?.message || "Failed to create job");
    } finally {
      setBusy(false);
    }
  };

  const handleSmartIntakeCreate = async () => {
    const text = String(smartIntakeText || "").trim();
    if (!text) {
      setSmartIntakeMessage("Paste intake text first.");
      return;
    }
    setSmartIntakeBusy(true);
    setSmartIntakeMessage("");
    setError("");
    try {
      const result = await extractCrmIntake({ text });
      const job = result?.job && typeof result.job === "object" ? result.job : {};
      const contact = result?.contact && typeof result.contact === "object" ? result.contact : {};
      const name = firstText(
        contact.name,
        job.property_owner,
        job.homeowner,
        job.customer,
        job.insured,
        job.name,
        job.property_address,
        job.address
      );
      const created = await createJob({
        name: standardizeCapitalization(name || newJobName || "Untitled Job"),
        notes: text,
        intake_source: text,
        intake_json: result && typeof result === "object" ? result : null,
      });
      setNewJobName("");
      setSmartIntakeText("");
      setSmartIntakeMessage(`Smart Intake created ${standardizeCapitalization(created?.name || name || "job")}.`);
      await refresh();
    } catch (e) {
      setSmartIntakeMessage(e?.message || "Smart Intake failed.");
    } finally {
      setSmartIntakeBusy(false);
    }
  };

  const handleDeleteOne = async (jobId) => {
    const id = String(jobId || "").trim();
    if (!id) return;
    const ok = window.confirm("Delete this job? This removes related data tied to the job.");
    if (!ok) return;

    setBusyDelete(true);
    setError("");
    try {
      await deleteJob(id);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      await refresh();
    } catch (e) {
      setError(e?.message || "Failed to delete job");
    } finally {
      setBusyDelete(false);
    }
  };

  const handleDeleteSelected = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length < 1) return;
    const ok = window.confirm(`Delete ${ids.length} selected job(s)? This removes related data tied to each job.`);
    if (!ok) return;

    setBusyDelete(true);
    setError("");
    try {
      await deleteJobsBulk(ids);
      setSelectedIds(new Set());
      await refresh();
    } catch (e) {
      setError(e?.message || "Failed to delete selected jobs");
    } finally {
      setBusyDelete(false);
    }
  };

  const filtered = jobs.filter((j) => {
    if (!query.trim()) return true;
    const q = query.trim().toLowerCase();
    return String(j?.name || "").toLowerCase().includes(q);
  });

  const filteredIds = filtered.map((j) => String(j?.id || "")).filter(Boolean);
  const allFilteredSelected = filteredIds.length > 0 && filteredIds.every((id) => selectedIds.has(id));

  const toggleSelectAllFiltered = (checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        filteredIds.forEach((id) => next.add(id));
      } else {
        filteredIds.forEach((id) => next.delete(id));
      }
      return next;
    });
  };

  const toggleSelectRow = (jobId, checked) => {
    const id = String(jobId || "");
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <Card>
        <CardHeader>
          <h1 className="text-xl font-semibold text-slate-900">Jobs</h1>
          <p className="mt-1 text-sm text-slate-500">Create, browse, and delete estimate jobs.</p>
        </CardHeader>
        <CardBody className="space-y-4">
          {error ? <p className="rounded-lg border border-rose-200 bg-rose-50 p-2 text-sm text-rose-700">{error}</p> : null}
          <div className="flex flex-wrap items-center gap-2">
            <Input
              className="min-w-[260px]"
              placeholder="Search jobs by name"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <Button variant="secondary" onClick={refresh} disabled={loading || busy || busyDelete}>
              Refresh
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              className="min-w-[260px]"
              placeholder="New job name"
              value={newJobName}
              onChange={(e) => setNewJobName(e.target.value)}
            />
            <Button onClick={handleCreate} disabled={busy || busyDelete}>
              {busy ? "Creating..." : "Create Job"}
            </Button>
            <Link to="/app/build">
              <Button variant="ghost">Open Build Wizard</Button>
            </Link>
            <Link to="/mitigation">
              <Button variant="secondary">Open Mitigation Station</Button>
            </Link>
            <Link to="/app/personal-property">
              <Button variant="secondary">Open Contents Corner</Button>
            </Link>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <h2 className="text-sm font-semibold text-slate-900">Smart Intake</h2>
            <p className="mt-1 text-sm text-slate-500">Paste intake notes to create a job from extracted details.</p>
            <textarea
              className="mt-3 min-h-[96px] w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:border-red-300 focus:outline-none focus:ring-2 focus:ring-red-100"
              value={smartIntakeText}
              placeholder="Paste customer, claim, address, and loss notes"
              onChange={(e) => setSmartIntakeText(e.target.value)}
            />
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button onClick={handleSmartIntakeCreate} disabled={smartIntakeBusy || busyDelete || !smartIntakeText.trim()}>
                {smartIntakeBusy ? "Extracting..." : "Create From Smart Intake"}
              </Button>
              <Button variant="secondary" onClick={() => { setSmartIntakeText(""); setSmartIntakeMessage(""); }}>
                Clear
              </Button>
              {smartIntakeMessage ? (
                <span
                  className={
                    smartIntakeMessage.toLowerCase().includes("failed")
                      ? "text-sm text-rose-700"
                      : "text-sm text-slate-600"
                  }
                >
                  {smartIntakeMessage}
                </span>
              ) : null}
            </div>
          </div>

          {loading ? <p className="text-sm text-slate-500">Loading jobs...</p> : null}
          {!loading ? (
            <div className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                <span className="text-sm text-slate-600">
                  {selectedIds.size ? `${selectedIds.size} job(s) selected` : "Select jobs to delete more than one at a time."}
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleDeleteSelected}
                  disabled={busyDelete || selectedIds.size < 1}
                >
                  {busyDelete ? "Deleting..." : `Delete selected jobs (${selectedIds.size})`}
                </Button>
              </div>
              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-slate-600">
                        <input
                          type="checkbox"
                          checked={allFilteredSelected}
                          onChange={(e) => toggleSelectAllFiltered(e.target.checked)}
                          aria-label="Select all filtered jobs"
                        />
                      </th>
                      <th className="px-3 py-2 text-left text-slate-600">Name</th>
                      <th className="hidden px-3 py-2 text-center text-slate-600">Inv</th>
                      <th className="px-3 py-2 text-left text-slate-600">Delete</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length < 1 ? (
                      <tr>
                        <td colSpan={4} className="px-3 py-4 text-slate-500">
                          No jobs found.
                        </td>
                      </tr>
                    ) : (
                      filtered.map((job, idx) => {
                        const id = String(job?.id || "");
                        const isSelected = selectedIds.has(id);
                        const invoiced = Boolean(job?.invoiced || Number(job?.invoice_count || 0) > 0);
                        const invoiceCount = Number(job?.invoice_count || 0);
                        return (
                          <tr key={String(job?.id || `row-${idx}`)} className="border-t border-slate-100">
                            <td className="px-3 py-2">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => toggleSelectRow(id, e.target.checked)}
                                aria-label={`Select job ${id}`}
                              />
                            </td>
                            <td className="px-3 py-2 text-slate-900">
                              <span className="inline-flex items-center gap-2">
                                <Link
                                  to={`/app/crm?jobId=${encodeURIComponent(id)}`}
                                  className="font-medium text-slate-900 underline-offset-2 hover:text-red-700 hover:underline"
                                >
                                  {standardizeCapitalization(job?.name || "") || "(untitled)"}
                                </Link>
                                {invoiced ? (
                                  <span
                                    title={`${invoiceCount || 1} invoice${invoiceCount === 1 ? "" : "s"} created`}
                                    aria-label="Invoiced"
                                    className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-emerald-50 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200"
                                  >
                                    {"\u2713"}
                                  </span>
                                ) : null}
                              </span>
                            </td>
                            <td className="hidden px-3 py-2 text-center">
                              {invoiced ? (
                                <span
                                  title={`${invoiceCount || 1} invoice${invoiceCount === 1 ? "" : "s"} created`}
                                  aria-label="Invoiced"
                                  className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-emerald-50 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200"
                                >
                                  ✓
                                </span>
                              ) : null}
                            </td>
                            <td className="px-3 py-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeleteOne(id)}
                                disabled={busyDelete}
                              >
                                Delete
                              </Button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </CardBody>
      </Card>
    </div>
  );
}
